import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { BookOpen, Download, ShoppingCart } from 'lucide-react';
import { getImageUrl, getPdfUrl } from '../utils/imageHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicationsPage = () => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublications();
  }, []);

  const fetchPublications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/publications`);
      setPublications(response.data);
    } catch (error) {
      console.error('Failed to fetch publications');
    } finally {
      setLoading(false);
    }
  };

  const getPDFUrl = (url) => getPdfUrl(url, '');

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-4">ত্রিপুরা দর্পণ প্রকাশনা</h1>
            <h2 className="text-3xl mb-4">Tripura Darpan Publications</h2>
            <p className="text-xl text-red-100">
              আমাদের প্রকাশিত বই এবং পত্রিকা সংগ্রহ
            </p>
            <p className="text-lg text-red-100">
              Explore our collection of books and magazines
            </p>
          </div>
        </div>
      </div>

      {/* Publications Grid */}
      <div className="container mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading publications...</p>
          </div>
        ) : publications.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {publications.map((pub) => (
              <Card key={pub.id} className="overflow-hidden hover:shadow-xl transition-all duration-300">
                {/* Book Cover */}
                <div className="relative h-80 bg-gray-200 group overflow-hidden">
                  <img
                    src={getImageUrl(pub.cover_image_url)}
                    alt={pub.title_bengali}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/300x400?text=No+Cover'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Book Info */}
                <CardContent className="p-4">
                  <h3 className="font-bold text-lg line-clamp-2 mb-1 min-h-[3.5rem]">
                    {pub.title_bengali}
                  </h3>
                  {pub.title_english && (
                    <p className="text-sm text-gray-600 line-clamp-1 mb-2">
                      {pub.title_english}
                    </p>
                  )}
                  
                  <p className="text-sm text-gray-500 mb-3">
                    by <span className="font-semibold">{pub.author}</span>
                  </p>

                  <p className="text-sm text-gray-700 line-clamp-3 mb-3 min-h-[3.5rem]">
                    {pub.description_bengali}
                  </p>

                  <div className="flex justify-between items-center mb-3">
                    <div className="text-2xl font-bold text-red-600">
                      ₹{pub.price}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {pub.published_year}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    {pub.pdf_url && (
                      <Button
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        size="sm"
                        onClick={() => window.open(getPDFUrl(pub.pdf_url), '_blank')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="w-full border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                      size="sm"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Order Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <BookOpen className="w-24 h-24 text-gray-300 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-gray-700 mb-2">
              কোনো প্রকাশনা উপলব্ধ নেই
            </h3>
            <p className="text-gray-500">No publications available yet</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default PublicationsPage;
